import { useState } from "react";
import { Link } from "react-router-dom";
import { imgUrl, imgSrcSet } from "@/lib/api";
import { Clock, Video, Play, ImageOff, RotateCcw } from "lucide-react";
import { sectionLabel, shouldShowSectionBadge } from "@/lib/sections";
import { normText, stripHtmlPreserveBreaks } from "@/lib/articleText";
import { gazaDateTime } from "@/lib/dateGaza";
import { useActiveBreakingId } from "@/lib/breaking";
import { useLang } from "@/lib/i18n";
import BreakingBadge from "@/components/BreakingBadge";
import { reportMediaError } from "@/lib/readerEvents";

// Excerpt only when it adds information beyond the title (never a hidden duplicate).
function excerptRemainderIfDistinct(rawExcerpt, rawTitle) {
    const e = normText(stripHtmlPreserveBreaks(rawExcerpt || ""));
    const t = normText(rawTitle || "");
    if (!e) return "";
    if (!t) return e;
    if (e === t || t.includes(e)) return "";
    if (!e.startsWith(t)) return e;
    return e.slice(t.length).replace(/^[\s\-–—:،.؛]+/, "").trim();
}

// Card image with a compact, retryable failure state (never a blank box).
function CardImage({ src, alt, className, eager, testId, slug, sizes = "(min-width: 1024px) 33vw, 100vw" }) {
    const [failed, setFailed] = useState(false);
    const [nonce, setNonce] = useState(0);
    const { t } = useLang();
    if (failed) {
        return (
            <div className="card-media-error" data-testid={`${testId}-error`}>
                <ImageOff className="w-5 h-5" aria-hidden="true" />
                <span>{t("imgFail")}</span>
                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFailed(false); setNonce((n) => n + 1); }} data-testid={`${testId}-retry`}>
                    <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> {t("retry")}
                </button>
            </div>
        );
    }
    return (
        <img key={nonce} src={imgUrl(src, 640) + (nonce ? `${imgUrl(src, 640).includes("?") ? "&" : "?"}r=${nonce}` : "")} srcSet={imgSrcSet(src)} sizes={sizes} alt={alt} className={className}
            loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : undefined} decoding="async" onError={() => { setFailed(true); reportMediaError({ slug, url: src, kind: "image", errorKind: navigator.onLine === false ? "network_offline" : "load_failed" }); }} data-testid={testId} />
    );
}

// EN mode only: the story is still Arabic (translation pending/failed) — say so instead of passing it off as English.
const PendingTr = ({ article, lang, t, slug }) => (lang !== "ar" && article.translation && !article.translation.available
    ? <span className="pending-tr" dir="ltr" data-testid={`pending-tr-${slug}`}>{t("arOnly")} · {t("pendingTr")}</span> : null);

const PlayOverlay = ({ size = "md", slug }) => (
    <span className="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none" aria-hidden data-testid={`card-video-badge-${slug}`}>
        <span className={`${size === "lg" ? "w-16 h-16" : size === "sm" ? "w-7 h-7" : "w-12 h-12"} rounded-full bg-white/95 flex items-center justify-center shadow-md`}>
            <Play className={`${size === "lg" ? "w-8 h-8" : size === "sm" ? "w-3.5 h-3.5" : "w-6 h-6"} text-brand-red translate-x-[1px]`} fill="currentColor" />
        </span>
    </span>
);

export function NewsCard({ article, variant = "default" }) {
    const activeBreakingId = useActiveBreakingId();
    const { p, lang, t } = useLang();
    if (!article) return null;
    const isLarge = variant === "hero";
    const isCompact = variant === "compact";
    const hasCover = !!article.cover_image;
    const hasVideo = !!article.video_url;
    const hasMedia = hasCover || hasVideo;
    const showBadge = shouldShowSectionBadge(article.section_slug);
    const breaking = article.id === activeBreakingId;   // newest published article only
    const activeBreaking = breaking;
    const excerptOut = excerptRemainderIfDistinct(article.excerpt, article.title);
    const arOnly = lang !== "ar" && article.translation && !article.translation.available;
    const textDir = arOnly ? "rtl" : undefined;

    if (isCompact) {
        return (
            <Link to={`${p}/article/${article.slug}`} data-article-id={article.id} className="news-card group flex gap-3 py-3 px-2 -mx-2 rounded-md border-b border-gray-100 last:border-0" data-testid={`card-compact-${article.slug}`}>
                {hasCover ? (
                    <div className="relative w-20 h-20 shrink-0 overflow-hidden rounded-sm bg-gray-100">
                        <CardImage src={article.cover_image} alt={article.title} className="w-full h-full object-cover news-card-image" testId={`card-img-${article.slug}`} slug={article.slug} />
                        {hasVideo && <PlayOverlay size="sm" slug={article.slug} />}
                    </div>
                ) : hasVideo ? (
                    <div className="w-20 h-20 shrink-0 overflow-hidden rounded-sm bg-black/85 flex items-center justify-center" aria-hidden data-testid={`card-video-badge-${article.slug}`}>
                        <Video className="w-6 h-6 text-white/80" />
                    </div>
                ) : null}
                <div className="flex-1 min-w-0">
                    {breaking && <BreakingBadge active={activeBreaking} className="mb-1" testId={`breaking-${article.slug}`} />}
                    <PendingTr article={article} lang={lang} t={t} slug={article.slug} />
                    <h3 dir={textDir} className="news-title news-title--compact news-title-clamp-3 text-brand-black transition-colors">{article.title}</h3>
                    <div className="news-meta text-[12px] text-gray-500 flex items-center gap-2 flex-wrap"><Clock className="w-3 h-3 shrink-0" /> <time dateTime={article.published_at} className="whitespace-nowrap">{gazaDateTime(article.published_at, lang)}</time></div>
                </div>
            </Link>
        );
    }

    // Grid hint: media cards take two rows so short text cards can pack beside them (see .news-grid).
    const span = isLarge ? "md:col-span-2 md:row-span-2" : hasMedia ? "news-card--media" : "news-card--text";
    return (
        <Link to={`${p}/article/${article.slug}`} data-article-id={article.id} data-has-media={hasMedia ? "1" : "0"}
            className={`news-card group block bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800 ${span}`} data-testid={`card-${article.slug}`}>
            {hasCover ? (
                <div className={`relative overflow-hidden media-slot ${isLarge ? "!aspect-auto" : ""}`}>
                    <CardImage src={article.cover_image} alt={article.title} eager={isLarge} testId={`card-img-${article.slug}`} slug={article.slug}
                        className={isLarge
                            ? `block w-full h-auto mx-auto object-contain news-card-image ${hasVideo ? "max-h-[55vh] md:max-h-[60vh] lg:max-h-[480px]" : "max-h-[70vh] lg:max-h-[480px]"}`
                            : "w-full h-full object-contain news-card-image"} />
                    {hasVideo && <PlayOverlay size={isLarge ? "lg" : "md"} slug={article.slug} />}
                    {showBadge && <span className="absolute bottom-3 start-3 bg-black/70 text-white text-[11px] font-bold px-2 py-1 rounded-sm">{sectionLabel(article.section_slug, lang)}</span>}
                </div>
            ) : hasVideo ? (
                <div className="relative overflow-hidden media-slot flex items-center justify-center" data-testid={`card-video-badge-${article.slug}`}>
                    <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-md">
                        <Play className="w-7 h-7 text-brand-red translate-x-[2px]" fill="currentColor" />
                    </span>
                    {showBadge && <span className="absolute bottom-3 start-3 bg-black/70 text-white text-[11px] font-bold px-2 py-1 rounded-sm">{sectionLabel(article.section_slug, lang)}</span>}
                </div>
            ) : null}
            <div className={hasMedia ? "p-4 md:p-[22px]" : "p-4 md:p-5"}>
                {/* Badge row sits between media and title (media cards) or above the title (text cards); rendered only when needed */}
                {(breaking || (!hasMedia && showBadge)) && (
                    <div className="mb-2 flex items-center gap-2" data-testid={`badge-row-${article.slug}`}>
                        {breaking && <BreakingBadge active={activeBreaking} testId={`breaking-${article.slug}`} />}
                        {!hasMedia && showBadge && <span className="bg-gray-100 text-gray-700 text-[11px] font-bold px-2 py-1 rounded-sm">{sectionLabel(article.section_slug, lang)}</span>}
                    </div>
                )}
                <PendingTr article={article} lang={lang} t={t} slug={article.slug} />
                <h3 dir={textDir} className={`news-title news-card-title text-brand-black dark:text-gray-100 transition-colors ${isLarge ? "news-title--hero" : "news-title--card news-title-clamp-3"}`}>{article.title}</h3>
                {excerptOut && <p dir={textDir} className={`text-gray-600 dark:text-gray-400 ${hasMedia ? "line-clamp-2" : "line-clamp-3"} ${isLarge ? "text-base" : "text-sm"}`} style={{ marginTop: "12px", lineHeight: "1.7" }}>{excerptOut}</p>}
                <div className="news-meta flex items-center gap-3 flex-wrap text-[12px] text-gray-500 dark:text-gray-400" style={{ marginTop: hasMedia ? "14px" : "10px" }}>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3 shrink-0" /> <time dateTime={article.published_at} className="whitespace-nowrap">{gazaDateTime(article.published_at, lang)}</time></span>
                    {/* View counts are counted and shown in the admin panel only (hidden from visitors by owner decision). */}
                </div>
            </div>
        </Link>
    );
}
